import { useLingui } from "@lingui/react";
import {
	type CountryValue,
	type countryStates,
	getStateNameDescriptor,
	shortToLongStates,
} from "@offstreetparking/geography/state-names";
import { useMemo } from "react";
import { getRegionDisplayName } from "../../../utils";
import { FormSelect } from "../FormSelect";
import { getStateOptions } from "./getStateOptions";

type FormStateSelectProps = {
	name: string;
	label: string;
	country?: CountryValue;
	className?: string;
	size?: "xs" | "sm" | "md";
	hideError?: boolean;
	disabled?: boolean;
};

export const FormStateSelect = ({
	name,
	label,
	country,
	className,
	size,
	hideError,
	disabled,
}: FormStateSelectProps) => {
	const { i18n } = useLingui();
	const states = useMemo(() => getStateOptions(country), [country]);

	if (!states) {
		return null;
	}

	const countryLabel = (code: string) => getRegionDisplayName({ code, locale: i18n.locale });
	const stateLabel = (stateCountry: string, state: string) => {
		const descriptor = getStateNameDescriptor(stateCountry, state);
		return descriptor ? i18n._(descriptor) : state;
	};

	return (
		<>
			{/* Desktop view - Includes full names of states / provinces */}
			<FormSelect
				isRequired
				name={name}
				label={label}
				className={className}
				containerClassName="hidden sm:block"
				size={size}
				hideError={hideError}
				disabled={disabled}
			>
				<option value="" />
				{Object.keys(states).map((stateCountry) => (
					<optgroup key={stateCountry} label={countryLabel(stateCountry)}>
						{((states[stateCountry as (typeof countryStates)[number]] ?? []) as string[]).map((state) => (
							<option key={state} value={shortToLongStates[stateCountry]?.[state]}>
								{stateLabel(stateCountry, state)}
							</option>
						))}
					</optgroup>
				))}
			</FormSelect>
			{/* Mobile view - Includes short code of states / provinces */}
			<FormSelect
				isRequired
				name={name}
				id={`${name}-mobile`}
				label={label}
				className={className}
				containerClassName="block sm:hidden"
				size={size}
				hideError={hideError}
				disabled={disabled}
			>
				<option value="" />
				{Object.keys(states).map((stateCountry) => (
					<optgroup key={stateCountry} label={stateCountry}>
						{((states[stateCountry as (typeof countryStates)[number]] ?? []) as string[]).map((state) => (
							<option key={state} value={shortToLongStates[stateCountry]?.[state]}>
								{state}
							</option>
						))}
					</optgroup>
				))}
			</FormSelect>
		</>
	);
};
